import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { trackPublicClick } from '@/lib/publicTracking';
import { Shield, Eye, Building2, FileCheck } from 'lucide-react';
const heroImg = '/hero-sovereign.webp';
const heroVideo = '/hero-gold-video.mp4';

const HeroSection = () => {
  const { lang, t } = useLanguage();

  const authorityItems = [
    { icon: Shield, label: t.home.heroAuthRegulated },
    { icon: Eye, label: t.home.heroAuthOversight },
    { icon: Building2, label: t.home.heroAuthCustodian },
    { icon: FileCheck, label: t.home.heroAuthAudit },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={heroImg}
          // @ts-ignore – fetchPriority is valid HTML but not yet in React's types
          fetchPriority="high"
          className="w-full h-full object-cover scale-105"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/85 to-charcoal/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-charcoal/30" />
      </div>

      {/* Subtle gold accent line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px gold-gradient-bg opacity-40" />

      <div className="relative z-10 container py-40">
        <div className="max-w-3xl">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 80 }}
            transition={{ duration: 0.8 }}
          >
            <div className="h-[2px] gold-gradient-bg" />
          </motion.div>

          <motion.h1
            className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-display text-primary-foreground leading-[1.1] mb-6 sm:mb-8 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            {t.home.heroTitle}
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-primary-foreground/50 leading-relaxed mb-14 max-w-2xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            {t.home.heroSubtitle}
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-x-8 gap-y-4 mb-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {authorityItems.map((item, i) => (
              <motion.div
                key={item.label}
                className="flex items-center gap-2.5 text-base text-primary-foreground/40"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
              >
                <item.icon className="h-4 w-4 text-secondary/70" />
                <span className="text-sm tracking-wide">{item.label}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="flex flex-wrap gap-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
          >
            <a
              href="#documents"
              className="inline-flex items-center px-10 py-4 gold-gradient-bg text-charcoal text-sm font-semibold tracking-[0.1em] uppercase hover:opacity-90 transition-all duration-300 premium-shadow"
            >
              {t.home.heroDownload}
            </a>
            <Link
              to={`/${lang}/investor`}
              onClick={() => trackPublicClick({ action: 'investor_click', language: lang, source: 'hero' })}
              className="inline-flex items-center px-10 py-4 border border-primary-foreground/15 text-primary-foreground/60 text-sm font-semibold tracking-[0.1em] uppercase hover:border-secondary/50 hover:text-secondary transition-all duration-300"
            >
              {t.home.heroInvestor}
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
